// Показателите на справка „Фактури".
//
// Числата са за ЦЕЛИЯ период, не за филтрирания списък: търсачката и филтрите
// живеят в самата таблица и стесняват нея и реда „Общо". Така кутийките
// отговарят на „какво е издадено в периода", а редът „Общо" — на „какво е
// останало след филтъра"; двете бройки не си противоречат, защото казват
// различни неща.
//
// Кутийката „Анулирани" се рисува САМО когато в периода ИМА анулирана фактура.
// Нулата тук е вярна, но безсъдържателна: анулирането е рядко събитие и през
// почти всеки ден кутийката би заемала пета позиция само за да каже „нищо не
// се е случило". Появи ли се обаче, тя е нужна — и защото анулираното иска
// внимание, и защото обяснява защо сумите изглеждат високи: анулираните
// фактури СА в тях.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import { ReportKpiCard } from "../ReportKpiCard";
import { deltaPercent } from "../reportDelta";
import type { InvoicesSummary } from "./invoicesMath";

type Props = {
  summary: InvoicesSummary;
  /** Предходният равен период — `null`, когато сравнението е изключено. */
  previous: InvoicesSummary | null;
  isNarrow: boolean;
};

export const InvoicesKpis = ({ summary, previous, isNarrow }: Props) => {
  const { t } = useTranslation();

  const hasCancelled = summary.cancelledCount > 0;

  return (
    // Класовете са ЦЕЛИ литерали: Tailwind сканира изходния текст и не вижда
    // име, сглобено с template literal.
    <div
      className={`grid gap-2.5 ${
        isNarrow
          ? "grid-cols-2"
          : hasCancelled
            ? "grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1.2fr_1fr]"
            : "grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1.2fr]"
      }`}
    >
      <ReportKpiCard
        kicker={t("reportsPage.invoicesIssuedKpi")}
        value={String(summary.count)}
        sub={t("reportsPage.invoicesIssuedKpiSub")}
        delta={
          previous ? deltaPercent(summary.count, previous.count) : undefined
        }
        hero
      />

      <ReportKpiCard
        kicker={t("reportsPage.invoicesTaxableKpi")}
        value={fmtEuro(summary.taxable)}
        sub={t("reportsPage.invoicesTaxableKpiSub")}
      />

      <ReportKpiCard
        kicker={t("reportsPage.invoicesVatKpi")}
        value={fmtEuro(summary.vat)}
        sub={t("reportsPage.invoicesVatKpiSub")}
      />

      {/* Двата водещи показателя носят Δ. Иначе бутонът „Δ спрямо предишния
          период" би менял една-единствена кутийка и нямаше да си личи, че
          изобщо е натиснат. */}
      <ReportKpiCard
        kicker={t("reportsPage.invoicesGrandKpi")}
        value={fmtEuro(summary.grandTotal)}
        sub={t("reportsPage.invoicesGrandKpiSub")}
        delta={
          previous
            ? deltaPercent(summary.grandTotal, previous.grandTotal)
            : undefined
        }
        hero
      />

      {hasCancelled ? (
        <ReportKpiCard
          kicker={t("reportsPage.invoicesCancelledKpi")}
          value={String(summary.cancelledCount)}
          sub={t("reportsPage.invoicesCancelledKpiSub")}
          hot
        />
      ) : null}
    </div>
  );
};
